"use client"

import Link from "next/link"
import { usePathname } from "next/navigation"
import { ChevronDown, Menu } from "lucide-react"

import { HeavySafeLink } from "@/components/site/heavy-safe-link"
import { PortalNavMenuItems } from "@/components/site/portal-nav-menu-items"
import { SiteLogo } from "@/components/site/site-logo"
import { SiteAuthControls } from "@/components/site/site-auth-controls"
import { buttonVariants } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { SITE_NAV, SITE_SERVICES } from "@/lib/site-nav"
import { cn } from "@/lib/utils"

function navLinkClass(active: boolean) {
  return cn(
    "cursor-pointer px-1 py-1.5 text-sm font-medium whitespace-nowrap",
    "border-b-2 border-transparent transition-[color,border-color]",
    active
      ? "border-primary text-foreground"
      : "text-muted-foreground hover:border-primary hover:text-foreground",
  )
}

export function SiteHeader() {
  const pathname = usePathname()
  const servicesActive = pathname.startsWith("/services")

  return (
    <header className="sticky top-0 z-40 border-b border-border bg-background/95 backdrop-blur supports-backdrop-filter:bg-background/80">
      <div className="mx-auto flex h-14 max-w-6xl items-center gap-3 px-4 sm:px-6 md:grid md:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)]">
        <SiteLogo className="text-base sm:text-lg md:justify-self-start" />

        <nav
          className="hidden items-center justify-center gap-5 md:flex"
          aria-label="Главное меню"
        >
          <Link
            href={SITE_NAV.home.href}
            prefetch={false}
            className={navLinkClass(pathname === SITE_NAV.home.href)}
          >
            {SITE_NAV.home.label}
          </Link>
          <HeavySafeLink
            href={SITE_NAV.labels.href}
            className={navLinkClass(pathname.startsWith("/labels"))}
          >
            {SITE_NAV.labels.label}
          </HeavySafeLink>
          <HeavySafeLink
            href={SITE_NAV.km.href}
            className={navLinkClass(pathname.startsWith("/km"))}
          >
            {SITE_NAV.km.label}
          </HeavySafeLink>

          <DropdownMenu>
            <DropdownMenuTrigger
              className={cn(
                navLinkClass(servicesActive),
                "inline-flex items-center gap-1 outline-none",
              )}
            >
              Сервисы
              <ChevronDown className="h-3.5 w-3.5 shrink-0 opacity-70" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="center" className="min-w-56">
              {SITE_SERVICES.map((item) => (
                <DropdownMenuItem
                  key={item.href}
                  render={<Link href={item.href} prefetch={false} />}
                >
                  {item.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <Link
            href={SITE_NAV.support.href}
            prefetch={false}
            className={navLinkClass(pathname.startsWith("/support"))}
          >
            {SITE_NAV.support.label}
          </Link>
        </nav>

        <div className="ml-auto flex items-center justify-end gap-2 md:ml-0 md:justify-self-end">
          {/* Mobile menu */}
          <DropdownMenu>
            <DropdownMenuTrigger
              className={cn(
                buttonVariants({ variant: "outline", size: "icon-sm" }),
                "md:hidden",
              )}
              aria-label="Открыть меню"
            >
              <Menu className="h-4 w-4 shrink-0" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-56">
              <PortalNavMenuItems />
              <DropdownMenuSeparator />
              <SiteAuthControls mobile />
            </DropdownMenuContent>
          </DropdownMenu>

          <SiteAuthControls />
        </div>
      </div>
    </header>
  )
}
